import { EnrollmentCallOutcome } from '@zorro/clients';
import { Select } from '@zorro/design-system/v2/Select';
import { TextInput } from '@zorro/design-system/v2/TextInput';
import { Textarea } from '@zorro/design-system/v2/Textarea';
import { Stack } from '@zorro/web/design-system';
import { Controller, UseFormReturn } from 'react-hook-form';

import { useAnalytics } from '../../../Analytics';
import {
  EnrollmentCallActorRole,
  LogEnrollmentCallFormFields,
  enrollmentCallOutcomeLabels,
} from './LogEnrollmentCallFormUtils';

type Props = {
  logEnrollmentCallForm: UseFormReturn<LogEnrollmentCallFormFields>;
  onboardingPeriodId: string;
  actorRole: EnrollmentCallActorRole;
};

export const LogEnrollmentCallFormInputs = ({
  logEnrollmentCallForm,
  onboardingPeriodId,
  actorRole,
}: Props) => {
  const { sendEvent } = useAnalytics();
  const { control } = logEnrollmentCallForm;

  return (
    <Stack gap="var(--space-24)">
      <Controller
        control={control}
        name="calledWith"
        render={({ field, fieldState }) => (
          <TextInput
            {...field}
            required
            label="Who was the call with"
            placeholder="e.g. employee, spouse"
            error={fieldState.error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="outcome"
        render={({ field: { onChange, ...rest }, fieldState }) => (
          <Select
            {...rest}
            required
            label="Outcome"
            placeholder="Select an outcome"
            error={fieldState.error?.message}
            data={Object.values(EnrollmentCallOutcome).map((outcome) => ({
              label: enrollmentCallOutcomeLabels[outcome],
              value: outcome,
            }))}
            onChange={(value) => {
              const isNewOutcome = value && value !== rest.value;
              onChange(value);
              if (isNewOutcome) {
                void sendEvent('enrollment_call_outcome_selected', {
                  outcome: value,
                  onboardingPeriodId,
                  actorRole,
                });
              }
            }}
          />
        )}
      />
      <Controller
        control={control}
        name="notes"
        render={({ field: { value, ...rest } }) => (
          <Textarea
            {...rest}
            value={value ?? ''}
            label="Notes"
            placeholder="Add notes about the call"
            minRows={4}
          />
        )}
      />
    </Stack>
  );
};
